import * as React from "react";
import { Library, Plus, Sparkles, Upload } from "lucide-react";
import { DropdownMenu } from "@/components/ui";
import { cn } from "@/lib/utils";
import { UPLOAD_TYPES } from "../hooks/useBackgroundMutations";

interface AddBackgroundMenuProps {
  disabled: boolean;
  onChooseLibrary(): void;
  onUpload(file: File): void;
  onGenerate(): void;
  uploading: boolean;
  uploadProgress: number | null;
}

const RING_RADIUS = 14;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ProgressRing: React.FC<{ progress: number | null }> = ({ progress }) => (
  <svg viewBox="0 0 32 32" className="size-8 -rotate-90" role="progressbar" aria-valuenow={progress ?? undefined}>
    <circle cx="16" cy="16" r={RING_RADIUS} fill="none" strokeWidth="3" className="stroke-slate-200" />
    <circle
      cx="16"
      cy="16"
      r={RING_RADIUS}
      fill="none"
      strokeWidth="3"
      strokeLinecap="round"
      className="stroke-primary transition-[stroke-dashoffset]"
      strokeDasharray={RING_LENGTH}
      strokeDashoffset={RING_LENGTH * (1 - (progress ?? 0) / 100)}
    />
  </svg>
);

/** The "+" tile at the end of the tray: choose from the library, upload a photo, or generate more with AI. */
export const AddBackgroundMenu: React.FC<AddBackgroundMenuProps> = ({
  disabled,
  onChooseLibrary,
  onUpload,
  onGenerate,
  uploading,
  uploadProgress,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUpload(file);
  };

  return (
    <div className="shrink-0 snap-start">
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_TYPES.join(",")}
        className="hidden"
        onChange={onFile}
        data-testid="background-upload-input"
      />
      {uploading ? (
        <div className="flex size-16 items-center justify-center rounded-lg border border-dashed border-slate-300">
          <ProgressRing progress={uploadProgress} />
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenu.Trigger
            disabled={disabled}
            aria-label="Add a background"
            className={cn(
              "flex size-16 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-500 cursor-pointer hover:border-primary hover:text-primary",
              disabled && "cursor-not-allowed opacity-50 hover:border-slate-300 hover:text-slate-500"
            )}
          >
            <Plus className="size-5" aria-hidden="true" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start">
            <DropdownMenu.Item
              label="Choose from library"
              icon={<Library aria-hidden="true" />}
              onClick={onChooseLibrary}
            />
            <DropdownMenu.Item
              label="Upload a photo"
              icon={<Upload aria-hidden="true" />}
              onClick={() => inputRef.current?.click()}
            />
            <DropdownMenu.Item
              label="Generate more with AI"
              icon={<Sparkles aria-hidden="true" />}
              onClick={onGenerate}
            />
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
    </div>
  );
};
